import { action, makeObservable, observable, runInAction } from 'mobx';
import { Service } from 'typedi';
import { BaseViewModel } from '../../../../core/services/BaseViewModel';
import { Appl } from '../../../../core/Appl';
import { toLocalDate } from '../../../../core/services/Helper';
import { IRetirementCalcService } from '../RetirementCalcService';
import { MemberAutofillDataModel, RetirementCalcFormModel } from './RetirementCalcModels';
import {
  MbaseDetail,
  defaultReferenceData,
  CombinedEstimateResult,
  CombinedMembershipInput,
  calculateCombinedEstimate,
  friendlyCalculationError,
  isPepraTier,
  RetirementReferenceData,
  parseDate,
} from './engine/RetirementCalcEngine';
import { fromReferenceDataDto, RetirementReferenceDataDto } from './engine/data/remoteReferenceData';

const STORAGE_KEY = 'retirementCalc.form';

function dateText(v: unknown): string | undefined {
  if (!v) return undefined;
  const d = v instanceof Date ? v : new Date(String(v));
  if (isNaN(d.getTime())) return typeof v === 'string' ? v : undefined;
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v)) return v;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function emptyForm(): RetirementCalcFormModel {
  return {
    includeBen: false,
    includeTA: false,
    priorMemberships: [],
  };
}

@Service() // eslint-disable-next-line
export default class RetirementCalcViewModel extends BaseViewModel {
  // Set only after a successful server-side resolution on the authenticated
  // deep-link flow (routes retirement-calculator/estimate[/:memberId]).
  // Undefined means the public/anonymous flow: the form starts blank and
  // nothing is fetched. Never trust a memberId supplied by the caller —
  // see initAsync.
  @observable public memberId?: string;
  @observable public isLoadingMemberData = false;
  @observable public memberDataLoadFailed = false;
  // When the prefilled MBASE values were read; shown as "Data as of" so the
  // member knows to check them. Undefined when nothing came from MBASE.
  @observable public memberDataAsOf?: Date;

  // Reference tables the engine runs on. The database is the source of
  // truth: loadReferenceDataAsync starts on page load and every calculation
  // waits for it. The bundled static set is the starting value and the
  // fallback if the load fails or times out (referenceDataIssue says why).
  @observable.ref public referenceData: RetirementReferenceData = defaultReferenceData;
  @observable public referenceDataSource: 'bundled' | 'server' = 'bundled';
  @observable public referenceDataIssue?: string;
  @observable public isPreparingCalculation = false;
  private _referenceDataRequest?: Promise<void>;
  // One automatic attempt per page visit, so a backend outage costs at most
  // one request timeout instead of one per Calculate click.
  private _referenceDataAttempted = false;

  @observable public form: RetirementCalcFormModel = emptyForm();

  @observable public result?: CombinedEstimateResult;
  @observable public calculationError?: string;

  // Path of the form page the user came from, so "Edit Scenario" returns to
  // the same entry point (public vs. authenticated).
  public formPath = '/retirement-calculator';

  private _service: IRetirementCalcService;

  constructor() {
    super();
    this._service = Appl.Services.get<IRetirementCalcService>('IRetirementCalcService');
    makeObservable(this);
  }

  // Called once by the page on mount. `isAuthenticatedEntry` is true for both
  // retirement-calculator/estimate (no id yet — the URL the MyScers portal
  // redirect constructs) and retirement-calculator/estimate/:memberId (the
  // internal, post-resolution URL). Mirrors SPC's
  // SpcViewModel.validateAndVerifyUrl: the id in the URL, if any, is never
  // trusted by itself — it's only ever used to skip a redundant re-fetch
  // after we already resolved it ourselves. Identity always comes from
  // validateReferrerUrl() (portal-origin check) followed by a backend call
  // that resolves the member server-side from the current URL/session, the
  // same way SPC's getMemberInfoByUrlAsync does. `isAuthenticatedEntry`
  // false => public flow: leave the form blank for manual entry.
  @action public async initAsync(memberId?: string, isAuthenticatedEntry?: boolean): Promise<void> {
    // Not awaited: the form renders on the bundled tables meanwhile, and
    // Calculate waits for this (prepareCalculationAsync). A failed attempt is
    // retried when the page is visited again.
    this._referenceDataAttempted = false;
    void this.loadReferenceDataAsync();

    if (!isAuthenticatedEntry) {
      // Keep whatever is already in the form (coming back from the results
      // page); fall back to the session copy after a refresh.
      if (!this.form.dob && !this.form.tier) this.restoreForm();
      this.memberId = undefined;
      this.memberDataLoadFailed = false;
      this.memberDataAsOf = undefined;
      return;
    }

    // Already resolved this exact member in this session (e.g. this is the
    // remount triggered by our own navigate() below, or a refresh on the
    // id-bearing URL after a prior successful load) — skip re-fetching.
    if (memberId && this.memberId === memberId && !this.memberDataLoadFailed) {
      return;
    }

    // Returning from the results page (Edit Scenario): the referrer was
    // already validated and the form holds the user's scenario — keep it.
    if (this.form.dob || this.form.tier || this.restoreForm()) {
      return;
    }

    this.form = emptyForm();
    this.memberDataLoadFailed = false;
    this.memberDataAsOf = undefined;

    const referrerIsValid = await this.validateReferrerUrl();
    if (!referrerIsValid) {
      return;
    }

    runInAction(() => {
      this.isLoadingMemberData = true;
    });
    const result = await this._service.getMemberInfoByUrlAsync({ url: window.location.href });
    runInAction(() => {
      this.isLoadingMemberData = false;
      if (result.success) {
        const data = result.data as MemberAutofillDataModel;
        this.memberId = data.memberId;
        this.applyAutofillData(data);
        // Move to the id-bearing URL so refresh/bookmarking within this
        // session keeps working, without the external caller ever having
        // had to know or supply the id itself.
        if (data.memberId && data.memberId !== memberId) {
          this.navigate(`/retirement-calculator/estimate/${data.memberId}`);
        }
      } else {
        // No blocking modal here (unlike SPC): unlike SPC's flow, this page
        // has a manual-entry fallback, and the inline banner below already
        // tells the member autofill failed and they can continue by hand —
        // a modal on top would be redundant, and would fire on every visit
        // until the backend's member-info-by-url endpoint exists.
        this.memberDataLoadFailed = true;
      }
    });
  }

  // Loads the database tables once per session. Never rejects: on any
  // failure the bundled tables stay in use and referenceDataIssue says why.
  public loadReferenceDataAsync(): Promise<void> {
    if (this.referenceDataSource === 'server') return Promise.resolve();
    if (this._referenceDataRequest) return this._referenceDataRequest;
    if (this._referenceDataAttempted) return Promise.resolve();
    this._referenceDataAttempted = true;
    this._referenceDataRequest = (async () => {
      try {
        const response = await this._service.getReferenceDataAsync();
        if (!response.success) {
          throw new Error(`request failed (${response.error?.status ?? response.error?.title ?? 'no response'})`);
        }
        const data = fromReferenceDataDto(response.data as RetirementReferenceDataDto);
        runInAction(() => {
          this.referenceData = data;
          this.referenceDataSource = 'server';
          this.referenceDataIssue = undefined;
        });
      } catch (e) {
        const issue = e instanceof Error ? e.message : String(e);
        runInAction(() => { this.referenceDataIssue = issue; });
        console.warn(`Retirement calculator reference data unavailable, using bundled tables: ${issue}`);
      } finally {
        this._referenceDataRequest = undefined;
      }
    })();
    return this._referenceDataRequest;
  }

  // Calculate entry point for the form: waits for the database tables (or
  // their failure) so an estimate is never computed on the bundled fallback
  // while the real data is still on its way.
  public async calculateAsync(): Promise<boolean> {
    runInAction(() => { this.isPreparingCalculation = true; });
    try {
      await this.loadReferenceDataAsync();
    } finally {
      runInAction(() => { this.isPreparingCalculation = false; });
    }
    return this.calculate();
  }

  // Same MyScers-portal-referrer check SPC applies before trusting a
  // memberId deep link. Redirects to /noauthorization if the page wasn't
  // reached from the portal (skipped in local dev).
  @action private async validateReferrerUrl(): Promise<boolean> {
    if (Appl.Setting.EnvironmentName === 'local') {
      return true;
    }
    const portalUrl = Appl.Setting.MyScersPortalUrl?.toLowerCase();
    const fromPortal = (r?: string | null) => !!portalUrl && !!r && r.includes(portalUrl);
    let referrer = await Appl.Cache.getSessionAsync<string>('Referrer');
    if (!referrer) {
      referrer = document.referrer?.toLowerCase();
      if (fromPortal(referrer)) {
        await Appl.Cache.addOrUpdateSessionAsync('Referrer', referrer);
      }
    }
    if (!fromPortal(referrer)) {
      this.navigate('/noauthorization');
      return false;
    }
    return true;
  }

  // Maps whatever the MBASE lookup returned onto the flat form fields,
  // field-by-field rather than a wholesale assignment, so a partial response
  // still autofills what it can and leaves the rest for manual entry. The
  // shared interceptor turns JSON nulls into '', so blanks are skipped too.
  @action private applyAutofillData(data: MemberAutofillDataModel): void {
    const has = (v: unknown) => v !== undefined && v !== null && v !== '';
    const asOf = data.asOfDate ? new Date(data.asOfDate) : undefined;
    this.memberDataAsOf = asOf && !isNaN(asOf.getTime()) ? asOf : undefined;
    if (data.dob) this.form.dob = toLocalDate(new Date(data.dob));
    if (data.currentTier) this.form.tier = data.currentTier;
    if (data.currentServiceRaw) this.form.serviceRaw = data.currentServiceRaw;
    if (has(data.currentSickHours)) this.form.sickHours = Number(data.currentSickHours);
    if (has(data.currentCompensation)) this.form.comp = Number(data.currentCompensation);
    if (data.priorMemberships?.length) {
      this.form.priorMemberships = data.priorMemberships;
      this.form.hasPriorMembership = true;
    }
  }

  @action public setHasPriorMembership(has: boolean): void {
    this.form.hasPriorMembership = has;
    if (!has) this.form.priorMemberships = [];
    else if (!this.form.priorMemberships.length) this.addPriorMembership();
  }

  @action public addPriorMembership(): void {
    this.form.priorMemberships.push({ id: `pm${Date.now()}${this.form.priorMemberships.length}`, tier: '', serviceRaw: '' });
  }

  @action public removePriorMembership(id: string): void {
    this.form.priorMemberships = this.form.priorMemberships.filter(m => m.id !== id);
    if (!this.form.priorMemberships.length) this.form.hasPriorMembership = false;
  }

  @action public resetForm(): void {
    this.form = emptyForm();
    this.result = undefined;
    this.calculationError = undefined;
    try { sessionStorage.removeItem(STORAGE_KEY); } catch { /* storage unavailable */ }
  }

  private persistForm(): void {
    try {
      const f = this.form;
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
        ...f, dob: dateText(f.dob), ret: dateText(f.ret), benDob: dateText(f.benDob),
      }));
    } catch { /* storage unavailable — in-memory state still works */ }
  }

  // Reloads the last calculated form from the session (page refresh).
  @action public restoreForm(): boolean {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return false;
      this.form = { ...emptyForm(), ...JSON.parse(raw) };
      return true;
    } catch {
      return false;
    }
  }

  // Results page entry: recompute from the (restored) form if the in-memory
  // result is gone (e.g. after a refresh, which also reloads the reference
  // data). Returns false when there is nothing to show.
  public async ensureResultAsync(): Promise<boolean> {
    if (this.result) return true;
    if (!this.form.dob && !this.restoreForm()) return false;
    return this.calculateAsync();
  }

  // Runs the client-side calculation engine against the current form state.
  // Purely synchronous/local — the engine (engine/RetirementCalcEngine.ts) is
  // a pure-function port of the original standalone calculator's math with no
  // network dependency, so this stays a plain @action rather than async.
  // Native HTML5 `required` validation (see the form's fields) already
  // guarantees the core fields are present by the time onSubmit fires this;
  // the beneficiary fields are the one pair not covered by `required` (they
  // toggle disabled/enabled with includeBen), so they're checked here.
  @action public calculate(): boolean {
    this.result = undefined;
    this.calculationError = undefined;

    const f = this.form;
    // Dates are text when typed but Date objects when autofilled from MBASE;
    // dateText normalises both to yyyy-mm-dd.
    const dob = parseDate(dateText(f.dob));
    const ret = parseDate(dateText(f.ret));
    const benDob = f.includeBen ? parseDate(dateText(f.benDob)) : null;

    const override = !!f.mbaseDetailOverrideEnabled;
    const mbaseDetail: MbaseDetail | null = override ? {
      integratedServiceHours: f.mbaseIntegratedServiceHours ?? 0,
      nonIntegratedServiceHours: f.mbaseNonIntegratedServiceHours ?? 0,
      integratedSickHours: f.mbaseIntegratedSickHours ?? 0,
      nonIntegratedSickHours: f.mbaseNonIntegratedSickHours ?? 0,
    } : null;

    // With the MBASE override on, service years and sick hours come from the
    // override hours (as in the original calculator) — the plain Service
    // Credit / Sick Hours boxes are ignored.
    let serviceRaw = f.serviceRaw;
    let sickHours = f.sickHours ?? 0;
    if (mbaseDetail) {
      const serviceHours = Number(mbaseDetail.integratedServiceHours) + Number(mbaseDetail.nonIntegratedServiceHours);
      const sickHrs = Number(mbaseDetail.integratedSickHours) + Number(mbaseDetail.nonIntegratedSickHours);
      if (![mbaseDetail.integratedServiceHours, mbaseDetail.nonIntegratedServiceHours, mbaseDetail.integratedSickHours, mbaseDetail.nonIntegratedSickHours].every(h => Number(h) >= 0)) {
        this.calculationError = 'MBASE / RAP override hours must be zero or greater.';
        return false;
      }
      if (!(serviceHours > 0)) {
        this.calculationError = 'Enter Integrated Service and/or Non-Integrated Service hours when MBASE / RAP override is enabled.';
        return false;
      }
      if (f.mbaseRegularContributions !== undefined && !(f.mbaseRegularContributions >= 0)) {
        this.calculationError = 'Regular Employee Contributions must be zero or greater.';
        return false;
      }
      serviceRaw = (serviceHours / this.referenceData.serviceCreditsPerYear).toFixed(5);
      sickHours = sickHrs;
    }

    const missing = [
      !dob && 'Date of Birth',
      !ret && 'Planned Retirement Date',
      !f.tier && 'Current Plan / Tier',
      !serviceRaw && 'Service Credit at Retirement Date',
      f.comp === undefined && 'Estimated Final Monthly Compensation',
    ].filter(Boolean);
    if (missing.length) {
      this.calculationError = `Please complete all required fields before calculating an estimate: ${missing.join(', ')}.`;
      return false;
    }
    if (ret <= dob) {
      this.calculationError = 'Please enter valid Date of Birth and Planned Retirement Date values.';
      return false;
    }
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const priors = f.hasPriorMembership ? f.priorMemberships : [];
    const anyOverride = override || priors.some(m => m.overrideEnabled);
    if (!anyOverride && ret < today) {
      this.calculationError = 'For member estimates, Planned Retirement Date must be today or later.';
      return false;
    }
    if (anyOverride && ret < new Date(2004, 6, 1, 12)) {
      this.calculationError = 'Historical RAP assumptions are available beginning July 1, 2004. Please use a retirement date on or after July 1, 2004.';
      return false;
    }
    if (!override && !(Number(serviceRaw) >= 0 && Number(serviceRaw) < 100)) {
      this.calculationError = 'Service Credit at Retirement Date must be between 0 and 99.99999 years.';
      return false;
    }
    if (!(f.comp > 0)) {
      this.calculationError = 'Estimated Final Monthly Compensation must be greater than zero.';
      return false;
    }
    if (!override && f.sickHours !== undefined && !(f.sickHours >= 0)) {
      this.calculationError = 'Unused Sick Leave Hours must be zero or greater.';
      return false;
    }
    const taTierEligible = !!f.tier && !isPepraTier(f.tier);
    if (f.includeTA && taTierEligible && !((f.ssEstimate ?? 0) > 0)) {
      this.calculationError = 'Estimated Social Security Benefit must be greater than zero when Temporary Annuity is included.';
      return false;
    }
    if (f.includeBen && (!f.benRelationship || !benDob)) {
      this.calculationError = 'Please enter the beneficiary’s relationship and date of birth.';
      return false;
    }

    const memberships: CombinedMembershipInput[] = [{
      tier: f.tier,
      serviceRaw,
      isCurrent: true,
      mbaseDetail,
      regularContributions: mbaseDetail ? f.mbaseRegularContributions : undefined,
    }];
    const seenTiers = new Set([f.tier]);
    for (const [i, m] of priors.entries()) {
      const label = `Prior Membership ${i + 1}`;
      if (!m.tier) {
        this.calculationError = `${label}: please select a Plan / Tier.`;
        return false;
      }
      if (seenTiers.has(m.tier)) {
        this.calculationError = `Each Plan / Tier should be entered only once. Please remove the duplicate ${this.referenceData.tierConfig[m.tier]?.name || 'Plan / Tier'} entry.`;
        return false;
      }
      seenTiers.add(m.tier);
      let detail: MbaseDetail | null = null;
      let svc = m.serviceRaw;
      if (m.overrideEnabled) {
        detail = {
          integratedServiceHours: m.integratedServiceHours ?? 0,
          nonIntegratedServiceHours: m.nonIntegratedServiceHours ?? 0,
          integratedSickHours: m.integratedSickHours ?? 0,
          nonIntegratedSickHours: m.nonIntegratedSickHours ?? 0,
        };
        const hrs = [detail.integratedServiceHours, detail.nonIntegratedServiceHours, detail.integratedSickHours, detail.nonIntegratedSickHours].map(Number);
        if (!hrs.every(h => h >= 0)) {
          this.calculationError = `${label}: MBASE / RAP override hours must be zero or greater.`;
          return false;
        }
        if (!(hrs[0] + hrs[1] > 0)) {
          this.calculationError = `${label}: enter Integrated Service and/or Non-Integrated Service hours when MBASE / RAP override is enabled.`;
          return false;
        }
        if (m.regularContributions !== undefined && !(m.regularContributions >= 0)) {
          this.calculationError = `${label}: Regular Employee Contributions must be zero or greater.`;
          return false;
        }
        svc = ((hrs[0] + hrs[1]) / this.referenceData.serviceCreditsPerYear).toFixed(5);
      } else if (!m.serviceRaw || !(Number(m.serviceRaw) >= 0 && Number(m.serviceRaw) < 100)) {
        this.calculationError = `${label}: Service Credit must be between 0 and 99.99999 years.`;
        return false;
      }
      memberships.push({
        tier: m.tier,
        serviceRaw: svc,
        isCurrent: false,
        mbaseDetail: detail,
        regularContributions: detail ? m.regularContributions : undefined,
      });
    }

    try {
      this.result = calculateCombinedEstimate({
        memberships,
        comp: f.comp,
        sickHours,
        dob,
        ret,
        benDob,
        includeBen: f.includeBen,
        includeTA: f.includeTA && taTierEligible,
        ssEstimate: f.ssEstimate ?? 0,
        eligibilityOverride: f.eligibilityOverride,
      }, this.referenceData, f.benRelationship);
    } catch (err) {
      this.calculationError = friendlyCalculationError(err);
      return false;
    }
    this.persistForm();
    return true;
  }
}
